import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/Button';
import { ArrowRight, Clock, Coins, TrendingUp, Zap } from 'lucide-react';
interface SlideSalesFocusProps {
  onContinue: () => void;
}
type Mode = 'today' | 'future';
const STAGES = [{
  label: 'Stage I',
  tagline: 'Pilot',
  revLabel: 'Low rev · Fast decision',
  dealSize: '€5–10K',
  timeToClose: '4–7 weeks',
  today: 85,
  future: 10,
  openQuotesTotal: '€110K',
  companies: ['Nestlé HS', 'CSL Vifor', 'Bayer', 'Abbott', 'AstraZeneca', 'MSD', 'Sanofi', 'Novartis', 'Boehringer']
}, {
  label: 'Stage II',
  tagline: 'Expansion',
  revLabel: 'Mid rev · Considered',
  dealSize: '€25–60K',
  timeToClose: '3–6 months',
  today: 10,
  future: 20,
  openQuotesTotal: '€115K',
  companies: ['Solventum', 'Menarini', 'Roche', 'Leo Pharma']
}, {
  label: 'Stage III',
  tagline: 'Scale',
  revLabel: 'High rev · Strategic',
  dealSize: '€100K+',
  timeToClose: '4–8 months',
  today: 5,
  future: 30,
  openQuotesTotal: '€164K',
  companies: ['GSK']
}, {
  label: 'Stage IV',
  tagline: 'Enterprise',
  revLabel: 'High rev · Slow decision',
  dealSize: '€1M+',
  timeToClose: '12+ months',
  today: 0,
  future: 40,
  openQuotesTotal: '',
  companies: []
}] as const;
export function SlideSalesFocus({
  onContinue
}: SlideSalesFocusProps) {
  const [stage, setStage] = useState(0);
  const [mode, setMode] = useState<Mode>('today');
  useEffect(() => {
    const t1 = setTimeout(() => setStage(1), 250);
    const t2 = setTimeout(() => setStage(2), 700);
    const t3 = setTimeout(() => setStage(3), 1200);
    const t4 = setTimeout(() => setStage(4), 1700);
    const t5 = setTimeout(() => setMode('future'), 2600);
    const t6 = setTimeout(() => setMode('today'), 4400);
    return () => {
      ;
      [t1, t2, t3, t4, t5, t6].forEach(clearTimeout);
    };
  }, []);
  return <div className="h-full w-full flex flex-col px-8 py-7 md:px-16 md:py-9 max-w-[1400px] mx-auto overflow-y-auto" data-id="element-745" data-mp="13emi2kwpsr60">
      {/* Heading */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: stage >= 1 ? 1 : 0,
      y: stage >= 1 ? 0 : 20
    }} data-id="element-746" data-mp="tob1nyvugpi8">
        <p className="text-[#6C2BD9] text-xs md:text-sm font-bold uppercase tracking-[0.2em]" data-id="element-747" data-mp="knmpzatxb18i">
          Why we're raising
        </p>
        <h2 className="text-3xl md:text-5xl font-sans font-bold text-slate-900 tracking-tight leading-tight mt-2" data-id="element-748" data-mp="oq9kxklw529x">
          From <span className="text-[#6C2BD9]" data-id="element-749" data-mp="ar03yipv244w">pilot</span> to{' '}
          <span className="text-[#6C2BD9]" data-id="element-750" data-mp="fo0byz69tgyn">enterprise penetration</span>
        </h2>
        <p className="text-slate-600 text-base md:text-lg mt-2 max-w-3xl" data-id="element-751" data-mp="253pyn2o1zhdc">
          Today we live off Stage I — fast revenue, but small. Investment gives
          us the liquidity to push Stage II clients into Stage III, where deals
          are 10× larger.
        </p>
      </motion.div>

      {/* Pilot → Enterprise arrow strip */}
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: stage >= 2 ? 1 : 0
    }} className="mt-6 flex items-center gap-3" data-id="element-752" data-mp="3pmk40o68ehyv">
        <span className="text-xs uppercase tracking-widest font-bold text-slate-500" data-id="element-753" data-mp="3d9al7dpys0r">
          Pilot
        </span>
        <div className="flex-1 h-px bg-gradient-to-r from-slate-300 via-[#6C2BD9]/40 to-[#6C2BD9]" data-id="element-754" data-mp="h44e045g8gwy" />
        <ArrowRight className="w-4 h-4 text-[#6C2BD9]" data-id="element-755" data-mp="c62slckjw5ib" />
        <span className="text-xs uppercase tracking-widest font-bold text-[#6C2BD9]" data-id="element-756" data-mp="r4epnkbu5ga0">
          Enterprise Penetration
        </span>
      </motion.div>

      {/* Mode toggle */}
      <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: stage >= 2 ? 1 : 0,
      y: stage >= 2 ? 0 : 10
    }} className="mt-5 flex items-center justify-center" data-id="element-757" data-mp="2sv8d0ts2f1sk">
        <div className="inline-flex bg-slate-100 border border-slate-200 rounded-full p-1 relative" data-id="element-758" data-mp="1eyoucw0tlhnj">
          <button onClick={() => setMode('today')} style={{
          color: mode === 'today' ? '#ffffff' : '#475569'
        }} className="relative z-10 px-5 md:px-6 py-2 rounded-full text-sm font-semibold transition-colors" data-id="element-759" data-mp="1jwqdti25rizj">
            <span style={{
            color: mode === 'today' ? '#ffffff' : '#475569'
          }} className="flex items-center gap-2" data-id="element-760" data-mp="wa0cyk1jpugi">
              <Zap className="w-3.5 h-3.5" style={{
              color: mode === 'today' ? '#ffffff' : '#475569'
            }} data-id="element-761" data-mp="158ccxcoay4lm" />
              Today · Liquidity mode
            </span>
          </button>
          <button onClick={() => setMode('future')} style={{
          color: mode === 'future' ? '#ffffff' : '#475569'
        }} className="relative z-10 px-5 md:px-6 py-2 rounded-full text-sm font-semibold transition-colors" data-id="element-762" data-mp="hjt4mx595rtj">
            <span style={{
            color: mode === 'future' ? '#ffffff' : '#475569'
          }} className="flex items-center gap-2" data-id="element-763" data-mp="4z4732z9v6hv">
              <TrendingUp className="w-3.5 h-3.5" style={{
              color: mode === 'future' ? '#ffffff' : '#475569'
            }} data-id="element-764" data-mp="3s5n5t45hexca" />
              With investment · Growth mode
            </span>
          </button>
          <motion.div layout transition={{
          type: 'spring',
          stiffness: 400,
          damping: 30
        }} className="absolute top-1 bottom-1 rounded-full bg-[#6C2BD9] shadow-md" style={{
          left: mode === 'today' ? 4 : '50%',
          right: mode === 'today' ? '50%' : 4
        }} data-id="element-765" data-mp="3dhtnhbp0hgq0" />
        </div>
      </motion.div>

      {/* 4-stage grid */}
      <div className="flex-1 grid grid-cols-4 gap-3 md:gap-4 mt-6" data-id="element-766" data-mp="2usghv9xfkaqc">
        {STAGES.map((s, idx) => {
        const value = mode === 'today' ? s.today : s.future;
        const isPrimaryFocus = mode === 'today' && idx === 0 || mode === 'future' && (idx === 1 || idx === 2);
        return <motion.div key={s.label} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: stage >= 3 ? 1 : 0,
          y: stage >= 3 ? 0 : 20
        }} transition={{
          duration: 0.4,
          delay: idx * 0.08
        }} className={`relative rounded-2xl p-4 md:p-5 flex flex-col border transition-colors ${isPrimaryFocus ? 'bg-[#F5F0FF] border-[#6C2BD9]' : 'bg-white border-slate-200'}`} data-id="element-767" data-mp="38l62pb1839lv" data-mpi={idx}>
              <div className="flex items-baseline justify-between" data-id="element-768" data-mp="1ku6wtc9oiz9o">
                <div data-id="element-769" data-mp="1ksdoztnby903">
                  <p className="text-[10px] md:text-[11px] uppercase tracking-widest font-bold text-slate-500" data-id="element-770" data-mp="1fvwzav0hfmkw">
                    {s.label}
                  </p>
                  <h4 className={`font-bold text-lg md:text-xl mt-0.5 ${isPrimaryFocus ? 'text-[#6C2BD9]' : 'text-slate-900'}`} data-id="element-771" data-mp="1ummf6aow1pv3">
                    {s.tagline}
                  </h4>
                </div>
                <span className={`text-[10px] uppercase tracking-wider font-bold ${isPrimaryFocus ? 'text-[#6C2BD9]' : 'text-slate-400'}`} data-id="element-772" data-mp="wxay7g4ytvgz">
                  {idx === 0 ? 'I' : idx === 1 ? 'II' : idx === 2 ? 'III' : 'IV'}
                </span>
              </div>

              <div className="mt-4 flex items-end gap-1" data-id="element-773" data-mp="2i9ktb1pjclpu">
                <motion.span key={`${s.label}-${mode}`} initial={{
              opacity: 0,
              y: 6
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.4
            }} className={`text-4xl md:text-5xl font-bold tabular-nums tracking-tight ${isPrimaryFocus ? 'text-[#6C2BD9]' : 'text-slate-700'}`} data-id="element-774" data-mp="fnz03ywdf4ze">
                  {value}
                </motion.span>
                <span className={`text-xl md:text-2xl font-bold mb-1 ${isPrimaryFocus ? 'text-[#6C2BD9]' : 'text-slate-500'}`} data-id="element-775" data-mp="18bl2j3tv43ko">
                  %
                </span>
                <span className={`ml-auto text-xs md:text-sm uppercase tracking-wider font-bold mb-1.5 ${isPrimaryFocus ? 'text-[#6C2BD9]' : 'text-slate-700'}`} data-id="element-776" data-mp="3a40tnni7jpqb">
                  of focus
                </span>
              </div>

              <div className="mt-2 h-2 rounded-full bg-slate-200 overflow-hidden" data-id="element-777" data-mp="1w9ap1q3wns3b">
                <motion.div className={`h-full rounded-full ${isPrimaryFocus ? 'bg-[#6C2BD9]' : 'bg-slate-400'}`} animate={{
              width: `${value}%`
            }} transition={{
              type: 'spring',
              stiffness: 120,
              damping: 20
            }} data-id="element-778" data-mp="1remvvdxyi9ie" />
              </div>

              <p className="text-xs text-slate-500 mt-3 leading-snug" data-id="element-779" data-mp="2b2vvua5qrjsg">
                {s.revLabel}
              </p>

              <div className="mt-4 pt-3 border-t border-slate-200 space-y-1.5" data-id="element-780" data-mp="216sv98qjft7m">
                <div className="flex items-center justify-between text-xs" data-id="element-781" data-mp="uqgwp6jt09bi">
                  <span className="flex items-center gap-1.5 text-slate-500" data-id="element-782" data-mp="urrxqylv72yx">
                    <Coins className="w-3 h-3" data-id="element-783" data-mp="pqm2ou4qf3m8" />
                    Order size
                  </span>
                  <span className="font-bold text-slate-900" data-id="element-784" data-mp="kch8t85f9hsp">{s.dealSize}</span>
                </div>
                <div className="flex items-center justify-between text-xs" data-id="element-785" data-mp="fefsuw3alylg">
                  <span className="flex items-center gap-1.5 text-slate-500" data-id="element-786" data-mp="u2u33s31gpc1">
                    <Clock className="w-3 h-3" data-id="element-787" data-mp="pair3qv0xd34" />
                    Time to close
                  </span>
                  <span className="font-bold text-slate-900" data-id="element-788" data-mp="131nxrcwt75my">
                    {s.timeToClose}
                  </span>
                </div>
              </div>

              {s.companies.length > 0 && <div className="mt-3 pt-3 border-t border-slate-200" data-id="element-789" data-mp="1czlj9snaaobv">
                  <div className="flex flex-wrap gap-1.5" data-id="element-790" data-mp="181tie94nvrvj">
                    {s.companies.map(c => <span key={c} className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold border ${isPrimaryFocus ? 'bg-white border-[#6C2BD9]/30 text-[#6C2BD9]' : 'bg-slate-50 border-slate-200 text-slate-700'}`} data-id="element-791" data-mp="3erhsuwpw5o40">
                        {c}
                      </span>)}
                  </div>
                </div>}

              {s.label === 'Stage IV' && <div className="mt-3 pt-3 border-t border-slate-200 space-y-2" data-id="element-792" data-mp="1kjmc5yc44uau">
                  <div className="flex items-baseline justify-between" data-id="element-793" data-mp="16kecz5fy9m6n">
                    <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500" data-id="element-794" data-mp="16mhccg2zsro1">
                      Combined quotes
                    </p>
                    <p className="text-[11px] font-bold text-[#6C2BD9]" data-id="element-795" data-mp="wn8m9yb8zx6y">
                      €389K
                    </p>
                  </div>
                  <div className="flex items-baseline justify-between" data-id="element-796" data-mp="11n4qnynuxqn1">
                    <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500" data-id="element-797" data-mp="2r0rcso6v2fir">
                      Combined full potential
                    </p>
                    <p className="text-[11px] font-bold text-[#6C2BD9]" data-id="element-798" data-mp="2uifdz9ds2o3y">
                      €22.5M
                    </p>
                  </div>
                </div>}
            </motion.div>;
      })}
      </div>

      <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: stage >= 4 ? 1 : 0,
      y: stage >= 4 ? 0 : 10
    }} className="mt-5 rounded-2xl border border-[#E4D7FF] bg-[#F5F0FF] px-5 py-4" data-id="element-799" data-mp="2kk804zescm66">
        <AnimatePresence mode="wait" data-id="element-800" data-mp="38a2clfv708pw">
          <motion.div key={mode} initial={{
          opacity: 0,
          y: 6
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -6
        }} transition={{
          duration: 0.3
        }} className="flex items-start gap-3" data-id="element-801" data-mp="33iu2sipyalc7">
            <div className="w-8 h-8 rounded-lg bg-[#6C2BD9] text-white flex items-center justify-center flex-shrink-0" data-id="element-802" data-mp="3c6a083tbghs">
              {mode === 'today' ? <Zap className="w-4 h-4" data-id="element-803" data-mp="7fchtk6kpfqd" /> : <TrendingUp className="w-4 h-4" data-id="element-804" data-mp="cbl1navptx3v" />}
            </div>
            <div className="flex-1" data-id="element-805" data-mp="hbpjp5c9uzw7">
              {mode === 'today' ? <>
                  <p className="text-sm font-bold text-slate-900" data-id="element-806" data-mp="mbbwxes3og50">
                    Today, 85% of our sales effort goes to Stage I deals.
                  </p>
                  <p className="text-sm text-slate-700 mt-1 leading-relaxed" data-id="element-807" data-mp="q9rhlc0plcf9">
                    Pilots close fast (4–7 weeks) and bring in cash, but they
                    cap at €5–10K. Out of necessity we chase what we can close —
                    leaving the bigger Stage II & III opportunities sitting in
                    the pipeline.
                  </p>
                </> : <>
                  <p className="text-sm font-bold text-slate-900" data-id="element-808" data-mp="26qyin3jyze68">
                    With investment, 90% of effort shifts beyond Stage I.
                  </p>
                  <p className="text-sm text-slate-700 mt-1 leading-relaxed" data-id="element-809" data-mp="21qkngm3gvypm">
                    Liquidity lets us run longer sales cycles and convert
                    existing relationships into Stage III (€100K+) and Stage IV
                    (€1M+) enterprise deals. Same accounts, 10× the deal size —
                    Pilot 10% · Expansion 20% · Scale 30% · Enterprise 40%.
                  </p>
                </>}
            </div>
          </motion.div>
        </AnimatePresence>
      </motion.div>

      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: stage >= 4 ? 1 : 0
    }} className="mt-4 flex justify-end" data-id="element-810" data-mp="1sryzucj51thz"></motion.div>
    </div>;
}